import { useState, PropsWithChildren } from 'react';
import cx from 'classnames';
import Checkbox from 'lib/Checkbox';
import Radio from 'lib/Radio';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import { TruncatedElementWithTooltip } from 'components/common/TruncatedElementWithTooltip/TruncatedElementWithTooltip';
import { CodeNameModel } from 'components/common/types';
import { SelectOptionProps } from './SelectOption.types';

const SelectOption: React.FC<PropsWithChildren<SelectOptionProps>> = ({
  isSelected,
  onClick,
  children,
  value,
  onIncludeExcludeChange,
  hasCheckbox = true,
  hasRadio = false,
  isIncludeExcludeAlwaysVisible = false,
  isDisabled = false,
  isMultiple = false,
  classNames,
  dataTestId = 'select-option',
  hideExclude,
  isDeselectAllowed,
  maxWidth = '250px',
}: SelectOptionProps) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const onHover = (newState: boolean): void => {
    if (isIncludeExcludeAlwaysVisible) return;
    setIsHovered(newState);
  };

  const onExcludeIncludeClick = (newValue: CodeNameModel): void => {
    if (!onIncludeExcludeChange) return;

    if (!isSelected || value.include !== newValue.include) {
      onIncludeExcludeChange(newValue);
    } else {
      onClick?.();
    }
  };

  return (
    <div
      data-test-id={dataTestId}
      className={cx(
        'flex rounded-sm p-2 mx-1 hover:bg-neutral-100 items-center',
        {
          'cursor-not-allowed': isDisabled && !isSelected,
        },
        classNames,
      )}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div
        className={cx('flex flex-1 min-w-0 items-center py-1', {
          'cursor-not-allowed text-neutral-400': isDisabled && !isSelected,
          'pl-1': hasCheckbox,
        })}
        onClick={() => {
          if (!onClick) return;

          if (!isDisabled && isMultiple) {
            onClick(value);
            return;
          }

          if (!isDisabled || isSelected) {
            onClick(value);
          }
        }}
        role="presentation"
      >
        {hasCheckbox && !hasRadio && <Checkbox isSelected={isSelected} />}
        {hasRadio && <Radio isSelected={isSelected} isDisabled={isDisabled} />}
        <TruncatedElementWithTooltip tooltip={children}>
          {(ref) => (
            <div
              className={cx('truncate flex-1 min-w-0', {
                'pl-2': hasCheckbox,
              })}
            >
              <div ref={ref} style={{ maxWidth }} className="truncate">
                {children}
              </div>
            </div>
          )}
        </TruncatedElementWithTooltip>
      </div>
      {onIncludeExcludeChange && (
        <IncludeExcludeToggle
          option={value}
          onIncludeExcludeChange={onExcludeIncludeClick}
          isHovered={isHovered}
          isSelected={isSelected}
          isAlwaysVisible={isIncludeExcludeAlwaysVisible}
          isOptionNameSelectable={hasCheckbox}
          hideExclude={hideExclude}
          isDeselectAllowed={isDeselectAllowed}
          isDisabled={isDisabled}
        />
      )}
    </div>
  );
};

export default SelectOption;
